import { useEffect, useMemo, useState, type ReactNode } from "react";
import OpenAI from "openai";
import { z } from "zod";
import { zodTextFormat } from "openai/helpers/zod";
import type {
  FunctionTool,
  ResponseFunctionToolCallOutputItem,
  ResponseInput,
  ResponseOutputItem,
  Tool,
} from "openai/resources/responses/responses.mjs";
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from "../components/ai-elements/conversation";
import { Check, CheckCircle, MessageSquare, RotateCcw } from "lucide-react";
import { Message, MessageContent } from "../components/ai-elements/message";
import {
  PromptInput,
  PromptInputBody,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputToolbar,
  PromptInputTools,
  type PromptInputMessage,
} from "../components/ai-elements/prompt-input";
import axios from "axios";
import { QueryClient } from "@tanstack/react-query";
import type { IngredientType, ItemWithIngredientsType } from "../types/item";
import type { CartItemType } from "../types/cart";
import ReactMarkdown from "react-markdown";
import { useCart } from "../contexts/CartProvider";
import mealPlaceholder from "@/assets/burger_placeholder.png";
import { Button } from "../components/ui/button";
import { useTranslation } from "react-i18next";
import { rawIngredientsToIngredientGroups } from "../utils/with";
import { Loader } from "../components/ai-elements/loader";
import { useParams } from "react-router";
import type { CompanyType } from "@/types/company";
import Bottleneck from "bottleneck";

const limiter = new Bottleneck({
  minTime: 2000,
  maxConcurrent: 1,
});

const limitedPost = limiter.wrap((url: string, data: any) =>
  axios.post(url, data)
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
  },
});

const idToUuidMap: Record<number, string> = {};

type ToolWithFunction = FunctionTool & {
  function: (...args: any[]) => Promise<any>;
};

type InputItemMessage = {
  type: "message";
  role: "user" | "assistant";
  content: string;
};

type InputItemFunctionCall = {
  type: "function_call";
  call_id: string;
  name: string;
  arguments: string;
};

type InputItemFunctionCallOutput = {
  type: "function_call_output";
  call_id: string;
  output: string;
};

type InputItemUi = {
  type: "ui";
  render: (...args: any[]) => ReactNode;
  parameters: any;
  text?: string;
};

type InputItem =
  | InputItemMessage
  | InputItemFunctionCallOutput
  | InputItemUi
  | InputItemFunctionCall;

const ItemsUI = ({
  ids,
  onItemClick,
}: {
  ids: number[];
  onItemClick: (item: any) => void;
}) => {
  const [items, setItems] = useState<any[]>([]);
  const [clickedOnItem, setClickedOnItem] = useState<any>(null);
  const { t } = useTranslation();

  const uuids = useMemo(() => ids.map((id) => idToUuidMap[id]), [ids]);

  const fetchItem = async (item_id: string) => {
    const response = await axios.get(
      `https://kaman.rest/api/item-details?item_id=${item_id}`
    );
    console.log("response.data.data.item", response.data);
    const item = response.data.data.item;
    return {
      id: item.id,
      name: item.name,
      price: item.price,
      image: item.image,
    };
  };

  useEffect(() => {
    (async () => {
      const items = await Promise.all(uuids.map(fetchItem));
      setItems(items);
    })();
  }, [uuids]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
      {items.map((item: any) => (
        <button
          disabled={!!clickedOnItem}
          key={item.id}
          className={`${
            clickedOnItem?.id === item.id
              ? "bg-green-100 border-green-500"
              : "border-gray-100 hover:border-gray-500 hover:bg-gray-200"
          } ${
            clickedOnItem ? "" : "cursor-pointer"
          } flex items-center gap-2 bg-gray-100 pe-4 rounded-lg border mb-2`}
          onClick={() => {
            setClickedOnItem(item);
            const numericId = Object.keys(idToUuidMap).find(
              (key) => idToUuidMap[parseInt(key)] === item.id
            );
            if (numericId) {
              onItemClick(parseInt(numericId));
            }
          }}
        >
          <img
            src={item.image || mealPlaceholder}
            alt={t("ai.item_image_alt")}
            className="w-16 h-16 rounded-lg object-cover"
          />
          <h1 className="text-md md:text-lg font-semibold">{item.name}</h1>
          <h1 className="text-md md:text-lg font-semibold ms-auto">
            {item.price}₪
          </h1>
        </button>
      ))}
    </div>
  );
};

const AddonsUI = ({
  item_id,
  onConfirmClick,
}: {
  item_id: number;
  onConfirmClick: (addons: string[]) => void;
}) => {
  const item_uuid = idToUuidMap[item_id];

  const { t } = useTranslation();

  const [itemWithIngredients, setItemWithIngredients] =
    useState<ItemWithIngredientsType | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<
    Record<string, string[]>
  >({});

  const [confirmClicked, setConfirmClicked] = useState(false);

  const handleAddonClick = (addonId: string) => {
    const group = itemWithIngredients?.ingredientGroups.find((group) =>
      group.ingredients.some((ingredient) => ingredient.id === addonId)
    );
    if (!group) return;

    // if the addon is already selected, remove it
    if (selectedAddons[group?.name]?.includes(addonId)) {
      setSelectedAddons((prev) => ({
        ...prev,
        [group?.name]: prev[group?.name]?.filter((id) => id !== addonId),
      }));
      return;
    }

    if (group.isMulti) {
      setSelectedAddons((prev) => ({
        ...prev,
        [group?.name]: [...(prev[group?.name] || []), addonId],
      }));
      return;
    } else {
      setSelectedAddons((prev) => ({
        ...prev,
        [group?.name]: [addonId],
      }));
      return;
    }
  };

  useEffect(() => {
    (async () => {
      const data = await queryClient.fetchQuery({
        queryKey: ["ai-item-details", item_uuid],
        queryFn: async () => {
          const response = await axios.get(
            `https://kaman.rest/api/item-details?item_id=${item_uuid}`
          );
          const data = response.data.data;
          return data;
        },
      });
      const itemWithIngredients: ItemWithIngredientsType = {
        id: data.item.id,
        name: data.item.name,
        description: data.item.description,
        price: data.item.price,
        image: data.item.image,
        companyName: data.item.company.name,
        companyId: data.item.company.id,
        categoryName: data.item.category.name,
        ingredientGroups: rawIngredientsToIngredientGroups(
          data.item_ingredients
        ),
      };
      if (itemWithIngredients.ingredientGroups.length === 0) {
        setConfirmClicked(true);
        onConfirmClick([]);
      }
      console.log("ItemWithIngredients: ", itemWithIngredients);
      setItemWithIngredients(itemWithIngredients);
    })();
  }, []);

  return (
    <div className="flex flex-col gap-4 mb-4">
      {itemWithIngredients?.ingredientGroups.map((group) => (
        <div className="flex flex-col gap-2">
          <h1>
            {group.name}{" "}
            {group.isMulti ? `(${t("pick_multiple")})` : `(${t("pick_one")})`}
          </h1>
          <div className="flex gap-2 flex-wrap">
            {group.ingredients.map((ingredient) => (
              <div
                className={`px-4 py-2 flex justify-center items-center rounded-lg border  cursor-pointer ${
                  selectedAddons[group.name]?.includes(ingredient.id)
                    ? "bg-green-100 border-green-500"
                    : "border-gray-300 hover:bg-gray-100 hover:border-gray-500"
                }`}
                onClick={() => handleAddonClick(ingredient.id)}
              >
                {ingredient.name} ({ingredient.price}₪)
              </div>
            ))}
          </div>
        </div>
      ))}
      <Button
        onClick={() => {
          setConfirmClicked(true);
          onConfirmClick(Object.values(selectedAddons).flat());
        }}
        className={`ms-auto w-48 ${
          confirmClicked && "bg-green-500 hover:bg-green-600"
        }`}
        size="lg"
        variant="default"
      >
        {confirmClicked ? <Check className="size-6" /> : t("confirm")}
      </Button>
    </div>
  );
};

const CategoriesUI = ({
  ids,
  onCategoryClick,
}: {
  ids: number[];
  onCategoryClick: (id: number) => void;
}) => {
  const { t } = useTranslation();
  const uuids = useMemo(() => ids.map((id) => idToUuidMap[id]), [ids]);

  const [categories, setCategories] = useState<any[]>([]);
  const [clickedOnCategory, setClickedOnCategory] = useState<any>(null);

  useEffect(() => {
    queryClient
      .fetchQuery({
        queryKey: ["ai-categories", uuids],
        queryFn: async () => {
          const response = await axios.get(
            `https://kaman.rest/api/categories?ids=${uuids.join(",")}`
          );
          return response.data.data.categories.map((category: any) => ({
            id: category.id,
            name: category.name,
            image: category.image,
          }));
        },
      })
      .then((categories) =>
        setCategories(
          categories
            .filter((category: any) => uuids.includes(category.id))
            .map((category: any) => ({
              id: category.id,
              name: category.name,
              image: category.image,
            }))
        )
      );
  }, [uuids]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
      {categories.map((category: any) => (
        <button
          key={category.id}
          disabled={!!clickedOnCategory}
          className={`${
            clickedOnCategory?.id === category.id
              ? "bg-green-100 border-green-500"
              : "border-gray-100 hover:border-gray-500 hover:bg-gray-200"
          } ${
            clickedOnCategory ? "" : "cursor-pointer"
          } flex items-center gap-2 bg-gray-100 p-4 rounded-lg border mb-2`}
          onClick={() => {
            setClickedOnCategory(category);
            const numericId = Object.keys(idToUuidMap).find(
              (key) => idToUuidMap[parseInt(key)] === category.id
            );
            if (numericId) {
              onCategoryClick(parseInt(numericId));
            }
          }}
        >
          <img
            src={category.image || mealPlaceholder}
            alt={t("ai.category_image_alt")}
            className="w-10 h-10 rounded-full"
          />
          <h1 className="text-lg font-semibold">{category.name}</h1>
        </button>
      ))}
    </div>
  );
};

const CompaniesUI = ({
  inputListIndex,
  ids,
  onCompanyClick,
}: {
  inputListIndex: number;
  ids: number[];
  onCompanyClick: (number: number) => void;
}) => {
  const { t } = useTranslation();
  const uuids = useMemo(() => ids.map((id) => idToUuidMap[id]), [ids]);

  const [companies, setCompanies] = useState<any[]>([]);

  const [clickedOnCompany, setClickedOnCompany] = useState<any>(null);

  useEffect(() => {
    queryClient
      .fetchQuery({
        queryKey: ["ai-companies", uuids],
        queryFn: async () => {
          const response = await axios.get(
            `https://kaman.rest/api/companies?ids=${uuids.join(",")}`
          );
          return response.data.data.companies.map((company: any) => ({
            id: company.id,
            name: company.name,
            logo: company.settings.logo,
          }));
        },
      })
      .then(setCompanies);
  }, [uuids]);

  const companiesToRender = companies.filter((company) =>
    uuids.includes(company.id)
  );

  return (
    <div className="grid grid-cols-2 gap-2 mb-4">
      {companiesToRender.map((company: any) => (
        <button
          key={company.id}
          className={`${
            clickedOnCompany?.id === company.id
              ? "bg-green-100 border-green-500"
              : "border-gray-100 "
          } ${
            !clickedOnCompany
              ? "hover:border-gray-500 hover:bg-gray-200 cursor-pointer"
              : ""
          } flex items-center gap-2 bg-gray-100 p-4 rounded-lg border mb-2`}
          onClick={() => {
            setClickedOnCompany(company);
            const numericId = Object.keys(idToUuidMap).find(
              (key) => idToUuidMap[parseInt(key)] === company.id
            );
            if (numericId) {
              onCompanyClick(parseInt(numericId));
            }
          }}
          disabled={!!clickedOnCompany}
        >
          <img
            src={company.logo || mealPlaceholder}
            alt={t("ai.company_logo_alt")}
            className="w-10 h-10 rounded-full"
          />
          <h1 className="text-xs truncate md:text-lg font-semibold">
            {company.name}
          </h1>
        </button>
      ))}
    </div>
  );
};
const instructions = `
  You are an AI assistant for a website called Kaman. Your primary goal is to help users order food.
  If the user prompts for unrelated topics, say that you are not able to help with that and ask them to ask questions related to kaman.

  CRITICAL CONTEXT: There is a major difference between 'main categories' and 'restaurant categories'.
  - Main categories are general, system-wide classifications of restaurants (e.g., "Burgers", "Pizza", "Sushi"). You CANNOT get items from a main category ID.
  - Restaurant categories are the specific menu sections of a single restaurant (e.g., "Appetizers", "Main Courses", "Desserts"). Item lists are retrieved using a restaurant category ID.

  When a user mentions a restaurant name, start by searching for the restaurant using 'search_restaurants_by_name', if no results are found, use 'get_restaurants' to get a list of restaurants and look through them and try to find the restaurant
  When a user wants to order a type of food ("burger", "pizza", etc), use 'search_items_by_name' to find the item, if no results are found, then use 'get_main_categories' to get a list of general food types and look through them and try to find the item.
  Always ask for addons before adding an item to the cart.
  Never show internal IDs to the user.

  When you want to show a list of restaurants, use 'render_restaurants' to display them to the user.
  When you want to show a list of items, use 'render_items' to display them to the user.
  When you want to show a list of categories, use 'render_categories' to display them to the user.
  When you want to show a list of addons, use 'render_addons' to display them to the user.
  DO NOT LIST THOSE AS TEXT, USE TOOLS INSTEAD.
  DO NOT LIST MORE THAN 10 ITEMS AT A TIME.


  if the user asks if a restaurant is open, use search_restaurants_by_name to find the restaurant, in the response there is a boolean open field

  the currency is Israeli Shekel (₪).

  User cannot pick a restaurant if it is not open.
  User cannot pick items from different restaurants, a cart only contains items from one restaurant.
  You can only call one tool at a time..
`;

const models = [
  {
    name: "gpt-4.1",
    tokenInputCost: 2 / 1000000,
    tokenOutputCost: 8 / 1000000,
  },
  {
    name: "gpt-4.1-mini",
    tokenInputCost: 0.4 / 1000000,
    tokenOutputCost: 1.6 / 1000000,
  },
  {
    name: "gpt-4o-mini",
    tokenInputCost: 0.15 / 1000000,
    tokenOutputCost: 0.6 / 1000000,
  },
  {
    name: "o4-mini",
    tokenInputCost: 1.1 / 1000000,
    tokenOutputCost: 4.4 / 1000000,
  },
  {
    name: "gpt-4.1-nano",
    tokenInputCost: 0.1 / 1000000,
    tokenOutputCost: 0.4 / 1000000,
  },
  {
    name: "gpt-5-mini",
    tokenInputCost: 0.25 / 1000000,
    tokenOutputCost: 2 / 1000000,
  },
  {
    name: "gpt-5-nano",
    tokenInputCost: 0.05 / 1000000,
    tokenOutputCost: 0.4 / 1000000,
  },
];

interface AiScreenProps {
  eventsOnly?: boolean;
}

export default function AiScreen({ eventsOnly = false }: AiScreenProps) {
  const [inputList, setInputList] = useState<InputItem[]>([]);
  const [input, setInput] = useState("");

  const [totalInputTokens, setTotalInputTokens] = useState(0);
  const [totalOutputTokens, setTotalOutputTokens] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [numberOfMessages, setNumberOfMessages] = useState(0);
  const [lastMessageInputTokens, setLastMessageInputTokens] = useState(0);
  const [lastMessageOutputTokens, setLastMessageOutputTokens] = useState(0);
  const [lastMessageCost, setLastMessageCost] = useState(0);
  const [modelName, setModelName] = useState("gpt-4o");

  const [toolInProgress, setToolInProgress] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [functionCallId, setFunctionCallId] = useState<string | null>(null);

  const { addToCart, openCart, changeCartItemQuantity, cartItems } = useCart();

  const { lang = "en" } = useParams();

  const { i18n } = useTranslation();

  useEffect(() => {
    if (["en", "he", "ar"].includes(lang)) {
      i18n.changeLanguage(lang);
    } else {
      i18n.changeLanguage("en");
    }
  }, [lang, i18n]);

  const get_restaurants = async ({
    limit,
    page,
  }: {
    limit: number;
    page: number;
  }) => {
    let companies = await queryClient.fetchQuery({
      queryKey: ["companies"],
      queryFn: async () => {
        const response = await axios.get(`https://kaman.rest/api/companies`);
        return response.data.data.companies;
      },
    });

    const isOpen = (company: any): { open: boolean; untill: string } => {
      const now = new Date();
      const day = now.getDay();
      const hours = now.getHours();
      const minutes = now.getMinutes();

      const currentTime = hours * 100 + minutes;

      const [openTime, closeTime] = company.settings.working_hours[day];
      console.log(openTime, closeTime, currentTime);

      if (!openTime) return { open: false, untill: "" };
      if (!closeTime) return { open: true, untill: "" };

      return {
        open: currentTime >= openTime && currentTime <= closeTime,
        untill: `${(closeTime / 100).toString().padStart(2, "0")}:${(
          closeTime % 100
        )
          .toString()
          .padStart(2, "0")}`,
      };
    };

    companies = companies.map((company: any) => {
      const numericId = Object.keys(idToUuidMap).length;
      idToUuidMap[numericId] = company.id;
      return {
        id: numericId,
        name: company.name,
        open: isOpen(company).open,
      };
    });
    return companies.slice((page - 1) * limit, page * limit);
  };

  const get_restaurants_by_categoryId = async ({
    category_id,
    limit,
    page,
  }: {
    category_id: number;
    limit: number;
    page: number;
  }) => {
    let companies = await queryClient.fetchQuery({
      queryKey: ["companies", category_id],
      queryFn: async () => {
        const response = await axios.get(
          `https://kaman.rest/api/companies?company_category=${category_id}`
        );
        return response.data.data.companies;
      },
    });
    companies = companies.map((company: any) => {
      const numericId = Object.keys(idToUuidMap).length;
      idToUuidMap[numericId] = company.id;
      return {
        id: numericId,
        name: company.name,
      };
    });
    return companies.slice((page - 1) * limit, page * limit);
  };

  const get_categories = async ({
    limit,
    page,
  }: {
    limit: number;
    page: number;
  }) => {
    const response = await axios.get(`https://kaman.rest/api/main-categories`);
    const categories = response.data.data.categories.map((category: any) => {
      const numericId = Object.keys(idToUuidMap).length;
      idToUuidMap[numericId] = category.id;
      return {
        id: numericId,
        name: category.name_en || category.name_ar || category.name_he,
      };
    });
    return categories.slice((page - 1) * limit, page * limit);
  };

  const search_items_by_name = async ({ name }: { name: string }) => {
    const response = await axios.get(
      `https://kaman.rest/api/items?search=${name}`
    );
    const items = response.data.data.items.map((item: any) => {
      const numericId = Object.keys(idToUuidMap).length;
      idToUuidMap[numericId] = item.id;
      return {
        id: numericId,
        name: item.name,
        price: item.price,
        restaurantName: item.company.name,
      };
    });
    return items;
  };

  const search_restaurants_by_name = async ({ name }: { name: string }) => {
    console.log("name to search for: ", name);
    const response = await axios.get(`https://kaman.rest/api/companies`);
    const companies = response.data.data.companies.map((company: any) => {
      const numericId = Object.keys(idToUuidMap).length;
      idToUuidMap[numericId] = company.id;
      return { id: numericId, name: company.name };
    });
    return companies.filter((company: any) =>
      company.name.toLowerCase().includes(name.toLowerCase())
    );
  };

  const get_restaruant_menu = async ({
    restaurant_id,
  }: {
    restaurant_id: number;
  }) => {
    const restaurant_uuid = idToUuidMap[restaurant_id];
    const response = await axios.get(
      `https://kaman.rest/api/categories?company_id=${restaurant_uuid}`
    );
    const categories = response.data.data.categories;
    return categories.map((category: any) => {
      const numericId = Object.keys(idToUuidMap).length;
      idToUuidMap[numericId] = category.id;
      return {
        id: numericId,
        name: category.name,
      };
    });
  };

  const get_item_details = async ({ item_id }: { item_id: number }) => {
    const item_uuid = idToUuidMap[item_id];

    const data = await queryClient.fetchQuery({
      queryKey: ["ai-item-details", item_id],
      queryFn: async () => {
        const response = await axios.get(
          `https://kaman.rest/api/item-details?item_id=${item_uuid}`
        );
        return response.data.data;
      },
    });

    const item = { ...data.item, ingredientGroups: data.item_ingredients };

    // Convert item_ingredients → addons
    const addons = (Object.values(item.ingredientGroups) as any[][])
      .map((options) => {
        if (options.length === 0) return null;

        return {
          selection_type:
            options[0].category_type === "single_option" ? "single" : "multi",
          options: options.map((opt) => ({
            id: opt.id,
            name: opt.name,
            price: opt.price,
          })),
        };
      })
      .filter(Boolean);

    return {
      id: item_id,
      name: item.name,
      price: "₪" + item.price,
      addons,
    };
  };

  const get_items_by_categoryId = async ({
    category_id,
  }: {
    category_id: number;
  }) => {
    const category_uuid = idToUuidMap[category_id];
    const response = await axios.get(
      `https://kaman.rest/api/items?category_id=${category_uuid}`
    );
    const items = response.data.data.items;
    return items
      .map((item: any) => {
        const numericId = Object.keys(idToUuidMap).length;
        idToUuidMap[numericId] = item.id;
        return {
          id: numericId,
          name: item.name,
          price: "₪" + item.price,
          addons: item.item_ingredients,
        };
      })
      .slice(0, 7);
  };

  const get_cart = async () => {
    if ((window as any).FlutterChannel) {
      (window as any).FlutterChannel.postMessage({
        type: "GET_CART",
      });
    }

    return cartItems.map((item) => {
      const numericId = Object.keys(idToUuidMap).length;
      idToUuidMap[numericId] = item.item.id;
      return {
        id: numericId,
        name: item.item.name,
        price: item.item.price,
        quantity: item.quantity,
        addons: item.ingredients.map((ingredient) => ({
          name: ingredient.name,
          price: ingredient.price,
          id: ingredient.id,
        })),
      };
    });
  };

  const change_cart_item_quantity = async ({
    item_id,
    quantity,
  }: {
    item_id: number;
    quantity: number;
  }) => {
    const item_uuid = idToUuidMap[item_id];

    if ((window as any).FlutterChannel) {
      (window as any).FlutterChannel.postMessage({
        type: "CHANGE_CART_ITEM_QUANTITY",
        payload: { item_id: item_uuid, quantity },
      });
    }

    if (eventsOnly) {
      return { done: true };
    }

    changeCartItemQuantity(
      cartItems.find((item) => item.item.id === item_uuid)!,
      quantity
    );

    render_cart_edited();

    return { done: true };
  };

  const add_to_cart = async ({
    items: inputItems,
  }: {
    items: {
      item_id: string;
      quantity: number;
      option_ids: string[];
    }[];
  }) => {
    inputItems = inputItems.map((item: any) => ({
      ...item,
      item_id: idToUuidMap[item.item_id],
    }));

    window.parent.postMessage(
      { type: "ADD_TO_CART", payload: inputItems },
      "*"
    );
    if ((window as any).FlutterChannel) {
      (window as any).FlutterChannel.postMessage(
        JSON.stringify({
          type: "ADD_TO_CART",
          payload: inputItems,
        })
      );
    }
    render_added_to_cart();

    if (eventsOnly) {
      return { done: true };
    }

    const cartItems: CartItemType[] = await Promise.all(
      inputItems.map(async (inputItem: any) => {
        const data = await queryClient.fetchQuery({
          queryKey: ["ai-item-details", inputItem.item_id],
          queryFn: async () => {
            const response = await axios.get(
              `https://kaman.rest/api/item-details?item_id=${inputItem.item_id}`
            );
            return response.data.data;
          },
        });

        console.log("data: ", data);

        return {
          item: {
            id: data.item.id,
            name: data.item.name,
            description: data.item.description,
            image: data.item.image,
            price: data.item.price,
            companyId: data.item.company.id,
            companyName: data.item.company.name,
            categoryId: data.item.category.id,
            categoryName: data.item.category.name,
            ingredientGroups: data.item_ingredients,
          },
          ingredients: Object.values(data.item_ingredients)
            .flatMap((group) => group)
            .filter((ingredient: any) =>
              inputItem.option_ids.includes(ingredient.id)
            ) as IngredientType[],
          quantity: inputItem.quantity,
        };
      })
    );

    const company_id = cartItems[0].item.companyId;

    const company = (
      await axios.get(
        `https://kaman.rest/api/company-info?company_id=${company_id}`
      )
    ).data.data.company;

    cartItems.forEach((cartItem) => {
      addToCart(company, cartItem);
    });

    return { done: true };
  };

  const render_categories = async ({
    ids,
    text,
  }: {
    ids: number[];
    text?: string;
  }): Promise<string> => {
    return new Promise<string>((resolve) => {
      setInputList((prev) => [
        ...prev,
        {
          type: "ui",
          render: () => (
            <CategoriesUI
              ids={ids}
              onCategoryClick={(id: number) =>
                resolve(`user picked ${id} show the items`)
              }
            />
          ),
          parameters: { ids },
          text,
        },
      ]);
    });
  };

  const render_items = async ({
    ids,
    text,
  }: {
    ids: number[];
    text?: string;
  }): Promise<string> => {
    return new Promise<string>((resolve) => {
      setInputList((prev) => [
        ...prev,
        {
          type: "ui",
          render: () => (
            <ItemsUI
              ids={ids}
              onItemClick={(id: number) =>
                resolve(`user picked ${id} show the addons`)
              }
            />
          ),
          parameters: { ids },
          text,
        },
      ]);
    });
  };

  const render_restaurants = async ({
    ids,
    text,
  }: {
    ids: number[];
    text?: string;
  }): Promise<string> => {
    return new Promise<string>((resolve) => {
      setInputList((prev) => [
        ...prev,
        {
          type: "ui",
          render: () => (
            <CompaniesUI
              inputListIndex={prev.length}
              ids={ids}
              onCompanyClick={(id: number) => {
                resolve(`user picked ${id} show the menu`);
              }}
            />
          ),
          parameters: { ids },
          text,
        },
      ]);
    });
  };

  const render_addons = async ({
    item_id,
    text,
  }: {
    item_id: number;
    text?: string;
  }): Promise<string> => {
    return new Promise<string>((resolve) => {
      setInputList((prev) => [
        ...prev,
        {
          type: "ui",
          render: () => (
            <AddonsUI
              item_id={item_id}
              onConfirmClick={(addons: string[]) =>
                resolve(`user confirmed addons: ${addons.join(", ")}`)
              }
            />
          ),
          parameters: { item_id },
          text,
        },
      ]);
    });
  };
  const { t } = useTranslation();

  const render_cart_edited = async () => {
    setInputList((prev) => [
      ...prev,
      {
        type: "ui",
        render: () => (
          <div
            className="cursor-pointer bg-green-100 p-4 rounded-lg border border-green-300 hover:border-green-500 hover:bg-green-200"
            onClick={() => {
              if ((window as any).FlutterChannel) {
                (window as any).FlutterChannel.postMessage(
                  JSON.stringify({
                    type: "OPEN_CART",
                  })
                );
              }
              window.parent.postMessage({ type: "OPEN_CART" }, "*");

              if (eventsOnly) {
                return;
              }

              openCart();
            }}
          >
            <div className="flex justify-between items-center">
              <h1 className="text-green-800 font-medium">
                {t("ai.cart_edited")}
              </h1>
              <CheckCircle className="size-5 text-green-800" />
            </div>
          </div>
        ),
        parameters: { text: "Cart edited" },
      },
    ]);
  };

  const render_added_to_cart = async () => {
    setInputList((prev) => [
      ...prev,
      {
        type: "ui",
        render: () => (
          <div
            className="cursor-pointer bg-green-100 p-4 rounded-lg border border-green-300 hover:border-green-500 hover:bg-green-200"
            onClick={() => {
              if ((window as any).FlutterChannel) {
                (window as any).FlutterChannel.postMessage(
                  JSON.stringify({
                    type: "OPEN_CART",
                  })
                );
              }
              window.parent.postMessage({ type: "OPEN_CART" }, "*");

              if (eventsOnly) {
                return;
              }

              openCart();
            }}
          >
            <div className="flex justify-between items-center">
              <h1 className="text-green-800 font-medium">
                {t("ai.added_to_cart")}
              </h1>
              <CheckCircle className="size-5 text-green-800" />
            </div>
          </div>
        ),
        parameters: { text: "Added to cart" },
      },
    ]);
  };

  const tools: any[] = [
    {
      type: "function",
      function: {
        strict: true,
        name: "get_restaurants",
        description: "get a list of restaurants",
        parameters: {
          type: "object",
          properties: {
            limit: { type: "number" },
            page: { type: "number" },
          },
          required: ["limit", "page"],
          additionalProperties: false,
        },
      },
      actualFunction: get_restaurants,
    },
    {
      type: "function",
      function: {
        strict: true,
        name: "get_restaurants_by_main_categoryId",
        description: "get a list of restaurants by main category id",
        parameters: {
          type: "object",
          properties: {
            category_id: { type: "number" },
            limit: { type: "number" },
            page: { type: "number" },
          },
          required: ["category_id", "limit", "page"],
          additionalProperties: false,
        },
      },
      actualFunction: get_restaurants_by_categoryId,
    },
    {
      type: "function",
      function: {
        strict: true,
        name: "get_main_categories",
        description:
          "get a list of main categories (system wide categories not for specific restaurants)",
        parameters: {
          type: "object",
          properties: {
            limit: { type: "number" },
            page: { type: "number" },
          },
          required: ["limit", "page"],
          additionalProperties: false,
        },
      },
      actualFunction: get_categories,
    },
    {
      type: "function",
      function: {
        strict: true,
        name: "search_restaurants_by_name",
        description: "search for a restaurant by name",
        parameters: {
          type: "object",
          properties: {
            name: { type: "string" },
          },
          required: ["name"],
          additionalProperties: false,
        },
      },
      actualFunction: search_restaurants_by_name,
    },
    {
      type: "function",
      function: {
        strict: true,
        name: "search_items_by_name",
        description: "search for an item by name",
        parameters: {
          type: "object",
          properties: {
            name: { type: "string" },
          },
          required: ["name"],
          additionalProperties: false,
        },
      },
      actualFunction: search_items_by_name,
    },

    {
      type: "function",
      function: {
        strict: true,
        name: "get_restaruant_menu",
        description: "get a list of categories for a restaurant",
        parameters: {
          type: "object",
          properties: {
            restaurant_id: { type: "number" },
          },
          required: ["restaurant_id"],
          additionalProperties: false,
        },
      },
      actualFunction: get_restaruant_menu,
    },
    {
      type: "function",
      function: {
        strict: true,
        name: "get_item_details",
        description: "get details for an item",
        parameters: {
          type: "object",
          properties: {
            item_id: { type: "number" },
          },
          required: ["item_id"],
          additionalProperties: false,
        },
      },
      actualFunction: get_item_details,
    },
    {
      type: "function",
      function: {
        strict: true,
        name: "get_items_by_restaurant_category_id",
        description: "get a list of items by restaurant_category id",
        parameters: {
          type: "object",
          properties: {
            category_id: { type: "number" },
          },
          required: ["category_id"],
          additionalProperties: false,
        },
      },
      actualFunction: get_items_by_categoryId,
    },
    {
      type: "function",
      function: {
        strict: true,
        name: "add_to_cart",
        description: "add an item to the cart",
        parameters: {
          type: "object",
          properties: {
            items: {
              type: "array",
              items: {
                type: "object",
                properties: {
                  item_id: { type: "string" },
                  quantity: { type: "number" },
                  option_ids: { type: "array", items: { type: "string" } },
                },
                required: ["item_id", "quantity", "option_ids"],
                additionalProperties: false,
              },
            },
          },
          required: ["items"],
          additionalProperties: false,
        },
      },
      actualFunction: add_to_cart,
    },
    {
      type: "function",
      function: {
        strict: true,
        name: "render_restaurants",
        description: "render a ui of a list of restaurants",
        parameters: {
          type: "object",
          properties: {
            ids: { type: "array", items: { type: "number" } },
            text: { type: "string" },
          },
          required: ["ids", "text"],
          additionalProperties: false,
        },
      },
      actualFunction: render_restaurants,
    },
    {
      type: "function",
      function: {
        strict: true,
        name: "render_categories",
        description: "render a ui of a list of categories",
        parameters: {
          type: "object",
          properties: {
            ids: { type: "array", items: { type: "number" } },
            text: { type: "string" },
          },
          required: ["ids", "text"],
          additionalProperties: false,
        },
      },
      actualFunction: render_categories,
    },
    {
      type: "function",
      function: {
        strict: true,
        name: "render_items",
        description: "render a ui of a list of items",
        parameters: {
          type: "object",
          properties: {
            ids: { type: "array", items: { type: "number" } },
            text: { type: "string" },
          },
          required: ["ids", "text"],
          additionalProperties: false,
        },
      },
      actualFunction: render_items,
    },
    {
      type: "function",
      function: {
        strict: true,
        name: "render_addons",
        description: "render a ui of a list of addons",
        parameters: {
          type: "object",
          properties: {
            item_id: { type: "number" },
            text: { type: "string" },
          },
          required: ["item_id", "text"],
          additionalProperties: false,
        },
      },
      actualFunction: render_addons,
    },
    {
      type: "function",
      function: {
        strict: true,
        name: "change_cart_item_quantity",
        description: "change the quantity of an item in the cart",
        parameters: {
          type: "object",
          properties: {
            item_id: { type: "number" },
            quantity: { type: "number" },
          },
          required: ["item_id", "quantity"],
          additionalProperties: false,
        },
      },
      actualFunction: change_cart_item_quantity,
    },
    // {
    //   type: "function",
    //   function: {
    //     strict: true,
    //     name: "get_cart",
    //     description: "get the cart",
    //     parameters: {
    //       type: "object",
    //       properties: {},
    //       required: [],
    //       additionalProperties: false,
    //     },
    //   },
    //   actualFunction: get_cart,
    // },
  ];

  const stats = (response: any) => {
    const data = response.data.data;
    const inputTokens = data.usage?.prompt_tokens ?? 0;
    const outputTokens = data.usage?.completion_tokens ?? 0;
    setTotalInputTokens((total) => total + inputTokens);
    setTotalOutputTokens((total) => total + outputTokens);

    const model = models.find((model) => model.name === modelName);
    if (!model) return;

    const cost =
      inputTokens * model.tokenInputCost + outputTokens * model.tokenOutputCost;
    setTotalCost((total) => total + cost);

    setLastMessageCost((messageCost) => messageCost + cost);
    setLastMessageInputTokens(
      (messageInputTokens) => messageInputTokens + inputTokens
    );
    setLastMessageOutputTokens(
      (messageOutputTokens) => messageOutputTokens + outputTokens
    );
  };

  const handleResponse = async (response: any) => {
    stats(response);

    const data = response.data.data;

    if (
      data.choices[0].message.content &&
      !data.choices[0].message.tool_calls
    ) {
      setIsLoading(false);
      setToolInProgress(null);
      setInputList((prev) => [
        ...prev,
        {
          type: "message",
          role: "assistant",
          content: data.choices[0].message.content || "",
        },
      ]);
    }
    let functionCalls = data.choices[0].message.tool_calls;

    if (functionCalls.length > 0) {
      functionCalls = [functionCalls[0]];
      setFunctionCallId(functionCalls.call_id);

      // append the function calls to the input list
      setInputList((prev) => {
        const newInputList: InputItem[] = [
          ...prev,
          ...functionCalls.map((call: any) => ({
            type: "function_call",
            role: "assistant",
            tool_calls: [
              {
                id: call.id,
                function: {
                  name: call.function.name,
                  arguments: call.function.arguments,
                },
                type: "function",
              },
            ],
          })),
        ];
        return newInputList;
      });

      const results: InputItem[] = await Promise.all(
        functionCalls.map(async (item: any) => {
          const tool = tools.find(
            (tool) => tool.function.name === item.function.name
          );
          if (tool?.function.name.includes("render")) {
            setIsLoading(false);
            setToolInProgress(null);
          } else {
            setToolInProgress("doing some magic");
          }
          const result = await tool?.actualFunction(
            JSON.parse(item.function.arguments)
          );
          console.log("result", result);
          console.log("JSON.stringify(result)", JSON.stringify(result));
          return {
            role: "tool",
            type: "function_call_output",
            tool_call_id: item.id,
            content: JSON.stringify(result),
          };
        })
      );

      setInputList((prev) => {
        const newInputList: InputItem[] = [...prev, ...results];
        callAI(newInputList);
        return newInputList;
      });
    }
  };

  useEffect(() => {
    console.log("inputList", inputList);
  }, [inputList]);

  const callAI = async (input: InputItem[]) => {
    setIsLoading(true);
    let last10 = input.filter((item) => item.type !== "ui").slice(-10);
    while (last10[0].type === "function_call_output") {
      last10 = last10.slice(1);
    }
    const toolsToSend = tools.map((tool) => {
      const { actualFunction: _, ...rest } = tool;
      return rest;
    });
    const response = await limitedPost("https://kaman.rest/api/ai-chat", {
      instructions: instructions + `\n answer in ${i18n.language}`,
      model: modelName,
      input: last10.map(({ type, ...rest }) => rest),
      tools: toolsToSend,
    });
    console.log("response", response);
    handleResponse(response);
  };

  const onSubmit = async (message: PromptInputMessage) => {
    setNumberOfMessages((numberOfMessages) => numberOfMessages + 1);
    setLastMessageInputTokens(0);
    setLastMessageOutputTokens(0);
    setLastMessageCost(0);

    setInput("");
    setInputList((prev) => {
      const newInputList: InputItem[] = [
        ...prev,
        {
          type: "message",
          role: "user",
          content: message.text || "",
        },
      ];
      callAI(newInputList);
      return newInputList;
    });
  };

  return (
    <div
      className="h-[calc(100vh-64px)] w-full relative flex flex-col"
      dir={i18n.language === "en" ? "ltr" : "rtl"}
      style={{
        fontFamily:
          i18n.language === "ar"
            ? "Tajawal, sans-serif"
            : i18n.language === "he"
            ? "Heebo, sans-serif"
            : "Poppins, sans-serif",
      }}
    >
      <div
        className={`w-full flex items-center justify-center mt-2 flex-shrink-0 ${
          inputList.length > 0 ? "" : "hidden"
        }`}
      >
        <button
          onClick={() => window.location.reload()}
          className="flex flex-shrink-0 items-center justify-center gap-2 border border-gray-300 rounded-full px-4 py-2 w-48 hover:bg-primary-light hover:border-primary hover:text-primary cursor-pointer"
        >
          <RotateCcw className="size-4" />
          <p className="text-sm text-center">{t("new_chat")}</p>
        </button>
      </div>
      <div className="max-w-4xl mx-auto md:p-6 px-2 pb-8 relative flex-1 min-h-0 w-full">
        <div className="flex flex-col h-full">
          <Conversation
            className="h-full w-full flex-1"
            dir={i18n.language === "en" ? "ltr" : "rtl"}
          >
            <ConversationContent>
              {inputList.length === 0 ? (
                <ConversationEmptyState
                  icon={<MessageSquare className="size-12" />}
                  title={t("ai.no_messages")}
                  description={t("ai.no_messages_description")}
                />
              ) : (
                inputList
                  .filter(
                    (item) =>
                      item.type !== "function_call_output" &&
                      item.type !== "function_call"
                  )
                  .map((message, index) => {
                    if (message.type === "ui") {
                      return (
                        <div
                          key={index.toString()}
                          className="flex flex-col gap-4"
                        >
                          {message.text && (
                            <ReactMarkdown>{message.text}</ReactMarkdown>
                          )}
                          {message.render(message.parameters)}
                        </div>
                      );
                    }

                    return message.role === "user" ? (
                      <Message from={message.role} key={index.toString()}>
                        <MessageContent variant="contained">
                          <ReactMarkdown>{message.content}</ReactMarkdown>
                        </MessageContent>
                      </Message>
                    ) : (
                      <Message from={message.role} key={index.toString()}>
                        <MessageContent variant="flat">
                          <ReactMarkdown>{message.content}</ReactMarkdown>
                        </MessageContent>
                      </Message>
                    );
                  })
              )}
            </ConversationContent>
            <ConversationScrollButton />
          </Conversation>

          {toolInProgress && (
            <div className="flex items-center justify-center gap-2 w-full ">
              <span className="text-sm text-gray-500 text-center ">
                {toolInProgress}
              </span>
              <Loader />
            </div>
          )}

          {isLoading && !toolInProgress && <Loader />}

          <PromptInput onSubmit={onSubmit} className="mt-4" globalDrop multiple>
            <PromptInputBody>
              <PromptInputTextarea
                maxLength={350}
                onChange={(e) => setInput(e.target.value)}
                value={input}
                placeholder={t("ai.placeholder")}
              />
            </PromptInputBody>
            <PromptInputToolbar>
              <PromptInputTools></PromptInputTools>
              <PromptInputSubmit
                disabled={isLoading}
                status={isLoading ? "submitted" : "ready"}
              />
            </PromptInputToolbar>
          </PromptInput>
        </div>
      </div>

      {import.meta.env.MODE === "development" && (
        <div
          dir="ltr"
          className="w-80 p-4 bg-white border border-gray-300 rounded-xl shadow-md absolute left-8 bottom-8 text-sm font-sans"
        >
          <h2 className="text-lg font-semibold mb-3">Model Usage Monitor</h2>

          <div className="mb-4 p-2 bg-gray-50 rounded-md">
            <p className="flex justify-between">
              <span className="font-medium">Model:</span>
              <span>{modelName}</span>
            </p>
            <p className="flex justify-between">
              <span className="font-medium">Total Input Tokens:</span>
              <span>{totalInputTokens}</span>
            </p>
            <p className="flex justify-between">
              <span className="font-medium">Total Output Tokens:</span>
              <span>{totalOutputTokens}</span>
            </p>
            <p className="flex justify-between">
              <span className="font-medium">Total Cost:</span>
              <span className="font-medium text-green-600">${totalCost}</span>
            </p>
          </div>

          <hr className="my-2 border-gray-200" />

          <div className="mb-4 p-2 bg-gray-50 rounded-md">
            <h3 className="font-medium mb-2">Per Message</h3>
            <div className="space-y-1">
              <p className="flex justify-between">
                <span className="font-medium">Input Tokens:</span>
                <span>{totalInputTokens / numberOfMessages}</span>
              </p>
              <p className="flex justify-between">
                <span className="font-medium">Output Tokens:</span>
                <span>{totalOutputTokens / numberOfMessages}</span>
              </p>
              <p className="flex justify-between">
                <span className="font-medium">Cost per Message:</span>
                <span className="font-medium text-green-600">
                  ${totalCost / numberOfMessages}
                </span>
              </p>
            </div>
          </div>

          <hr className="my-2 border-gray-200" />

          <div className="p-2 bg-gray-50 rounded-md">
            <h3 className="font-medium mb-2">Last Message</h3>
            <div className="space-y-1">
              <p className="flex justify-between">
                <span className="font-medium">Input Tokens:</span>
                <span>{lastMessageInputTokens}</span>
              </p>
              <p className="flex justify-between">
                <span className="font-medium">Output Tokens:</span>
                <span>{lastMessageOutputTokens}</span>
              </p>
              <p className="flex justify-between">
                <span className="font-medium">Cost:</span>
                <span className="font-medium text-green-600">
                  ${lastMessageCost}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
